import { Container } from '@/ui/design-system/Container'
import { Button } from '@/ui/components'
import { cn } from '@/shared/utils'
import { ProBadge } from '../ProBadge'
import { visibleFields } from '../wizard/wizardData'

const COLLAB_POINTS = [
  {
    title: 'A friends list built for crews',
    body: 'Keep your overlanding circle in one place and invite them to a trip in two taps. No phone-number roulette.',
  },
  {
    title: 'Live links, not snapshots',
    body: 'Swap a campsite or shift the dates and every link you already sent updates itself. Nobody drives to the old plan.',
  },
  {
    title: 'Revoke anytime',
    body: 'Audience changed? Kill the link. Your spots stay yours.',
  },
]

const OPEN_INVITE_PERMS = { gps: false, campsites: false, route: true, crew: false, notes: true }
const TRUSTED_CREW_PERMS = { gps: true, campsites: true, route: true, crew: true, notes: true }

function LinkPreviewCard({
  title,
  audience,
  perms,
  highlight,
}: {
  title: string
  audience: string
  perms: Record<'gps' | 'campsites' | 'route' | 'crew' | 'notes', boolean>
  highlight?: boolean
}) {
  const fields = visibleFields(perms)
  const shownCount = fields.filter((f) => f.visible).length
  return (
    <div
      className={cn(
        'flex-1 rounded-xl border p-4',
        highlight ? 'border-landing-glow/40 bg-landing-glow/5 shadow-glow-sm' : 'border-landing-line bg-landing-raised',
      )}
    >
      <div className="flex items-baseline justify-between gap-2">
        <p className={cn('text-[11px] font-semibold uppercase tracking-[0.15em]', highlight ? 'text-landing-glow-soft' : 'text-white/50')}>
          {title}
        </p>
        <p className="text-[10px] tabular-nums text-white/35">{shownCount} / {fields.length} fields</p>
      </div>
      <p className="mt-1 text-[11px] text-white/40">{audience}</p>
      <div className="mt-3 space-y-2.5">
        {fields.map((field) => (
          <div key={field.key}>
            <p className="text-[10px] uppercase tracking-[0.15em] text-white/30">{field.label}</p>
            {field.visible ? (
              <p className="truncate text-[13px] text-white/80">{field.value}</p>
            ) : (
              <p className="truncate text-[13px] italic text-white/35">{field.hiddenValue}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export function CollaborationSpotlight() {
  return (
    <section id="collaboration" className="scroll-mt-16 bg-landing-surface py-20 lg:py-28">
      <Container size="app">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          {/* Narrative */}
          <div className="flex flex-col gap-6">
            <h2
              className="text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl md:text-5xl"
              style={{ textWrap: 'balance' }}
            >
              Trips are better with a crew.{' '}
              <span className="text-landing-glow-soft">Share on your terms.</span>
            </h2>
            <p className="text-lg leading-relaxed text-white/65">
              Getting a crew aligned without giving away your hard-won spots is the part
              no one else solves. Every share link has its own permission set — the same
              link can show a trusted rig everything and a stranger almost nothing.
            </p>

            <ul className="space-y-5">
              {COLLAB_POINTS.map((point) => (
                <li key={point.title} className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-landing-glow" />
                  <span>
                    <span className="block text-base font-semibold text-white">{point.title}</span>
                    <span className="block text-base text-white/60">{point.body}</span>
                  </span>
                </li>
              ))}
            </ul>

            <div className="flex items-center gap-2 text-sm text-white/50">
              <ProBadge />
              <span>Per-link controls are Pro. Basic sharing is free.</span>
            </div>

            <div className="pt-2">
              <Button asChild size="lg" className="shadow-glow-sm">
                <a href="/signup">Start planning with your crew</a>
              </Button>
            </div>
          </div>

          {/* Same trip, two links */}
          <div>
            <p className="mb-3 text-sm font-medium text-white/50">One trip, two share links:</p>
            <div className="flex flex-col gap-4 sm:flex-row">
              <LinkPreviewCard
                title="Open invite"
                audience="For the forum thread — route shape only, no coordinates"
                perms={OPEN_INVITE_PERMS}
              />
              <LinkPreviewCard
                title="Trusted crew"
                audience="For the rigs actually coming — the whole plan, live"
                perms={TRUSTED_CREW_PERMS}
                highlight
              />
            </div>
            <p className="mt-4 text-[13px] leading-relaxed text-white/45">
              Both links point at the same trip you just built above. Change the plan once and
              every audience sees exactly what you allow — nothing more.
            </p>
          </div>
        </div>
      </Container>
    </section>
  )
}
